import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Agota Estate</title>
        <meta name="description" content="Privacy Policy for Agota Estate - Learn how we collect, use, and protect your personal information." />
      </Helmet>
      
      <Navbar />
      
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-8">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">Last updated: December 2024</p>
            
            <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Agota Estate ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. Please read this policy carefully to understand our practices regarding your personal data.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">2. Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">We may collect the following types of information:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Personal Information:</strong> Name, email address, phone number, and other contact details you provide when making inquiries.</li>
                  <li><strong>Property Preferences:</strong> Information about your property interests, budget range, and location preferences.</li>
                  <li><strong>Usage Data:</strong> Information about how you interact with our website, including pages visited and time spent.</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, and IP address for analytics purposes.</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">3. How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">We use the collected information for:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Responding to your property inquiries and providing personalized recommendations</li>
                  <li>Connecting you with our partner agencies (Sotheby's International Realty, Christie's International Real Estate, Knight Frank, and Savills)</li>
                  <li>Sending you relevant property updates and market insights (with your consent)</li>
                  <li>Improving our website and services</li>
                  <li>Complying with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">4. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may share your information with our trusted partner agencies to facilitate property viewings and transactions. We do not sell your personal information to third parties. We may disclose information when required by law or to protect our rights and safety.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">5. AI-Generated Content Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Some images displayed on our website are AI-generated for illustrative purposes only and may not represent actual properties. All property details, specifications, and availability should be verified directly with our team or partner agencies before making any decisions.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">6. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">7. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Access, correct, or delete your personal information</li>
                  <li>Withdraw consent for data processing</li>
                  <li>Request data portability</li>
                  <li>Object to certain processing activities</li>
                  <li>Lodge a complaint with a supervisory authority</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">8. Cookies</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie preferences through your browser settings.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">9. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at privacy@agotaestate.com or through our contact form.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
